'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/text';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import {
  readStringValue,
  toPrettyText,
  toRecord,
} from '../shared/network-tool-card-utils';
import { renderSseClientResult } from './sse-client-result-renderer';

function SseClientCard({ ctx }: { ctx: CardRenderContext }): ReactNode {
  const t = useTranslations('designSystem.chat.sseClient');
  const input = toRecord(ctx.toolCallData.input);
  const output = toRecord(ctx.toolCallData.output);
  const url =
    readStringValue(input, ['url', 'endpoint']) ??
    readStringValue(output, ['url', 'endpoint']);
  const sessionId =
    readStringValue(input, ['sessionId', 'session_id']) ??
    readStringValue(output, ['sessionId', 'session_id']);
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const error = ctx.toolCallData.error;
  const actionContent =
    url || sessionId
      ? [
          url ? `url: ${url}` : undefined,
          sessionId ? `session: ${sessionId}` : undefined,
        ]
          .filter(Boolean)
          .join('\n')
      : toPrettyText(ctx.toolCallData.input);
  const result = ctx.toolCallData.output
    ? renderSseClientResult({
        content: ctx.toolCallData.output,
        maxHeight: '260px',
      })
    : undefined;

  return (
    <FlatToolCard
      title={t('title')}
      isRunning={isRunning}
      durationMs={ctx.toolCallData.timing?.durationMs}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    >
      {actionContent ? (
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.muted"
          whiteSpace="pre-wrap"
          wordBreak="break-all"
        >
          {actionContent}
        </Text>
      ) : null}
      {error ? (
        <Text fontSize="xs" color="fg.error" whiteSpace="pre-wrap">
          {error}
        </Text>
      ) : (
        result
      )}
    </FlatToolCard>
  );
}

export function renderSseClientCard(ctx: CardRenderContext): ReactNode {
  return <SseClientCard ctx={ctx} />;
}
